import React, { useEffect, useState } from 'react';
import { runnerApi, errorText, type RunnerSchema, type RunnerSettings, type S3Info } from '../../../api/migrationRunner';
import { Btn, ErrorBox, Panel, Pill, Spinner } from './ui';

const Row: React.FC<{ title: string; hint: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 py-3 last:border-0">
    <div className="min-w-[240px] flex-1">
      <div className="text-[13.5px] font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500">{hint}</div>
    </div>
    {children}
  </div>
);

const sel = 'rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[13px] disabled:bg-slate-50';

/** How migrations run (approved mockup, "Settings"). */
const SettingsTab: React.FC<{ isOwner: boolean }> = ({ isOwner }) => {
  const [settings, setSettings] = useState<RunnerSettings | null>(null);
  const [saved, setSaved] = useState<RunnerSettings | null>(null);
  const [schema, setSchema] = useState<RunnerSchema | null>(null);
  const [s3, setS3] = useState<S3Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    runnerApi.settings()
      .then((r) => { setSettings(r.settings); setSaved(r.settings); setSchema(r.schema); setS3(r.s3); })
      .catch((err) => setError(errorText(err, 'Could not load settings')));
  }, []);

  if (!settings) return <div className="px-6 py-6">{error ? <ErrorBox>{error}</ErrorBox> : <Spinner />}</div>;

  const set = <K extends keyof RunnerSettings>(k: K, v: RunnerSettings[K]) => setSettings({ ...settings, [k]: v });
  const dirty = JSON.stringify(settings) !== JSON.stringify(saved);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const s = await runnerApi.saveSettings(settings);
      setSettings(s);
      setSaved(s);
      setFlash(true);
      setTimeout(() => setFlash(false), 2000);
    } catch (err) {
      setError(errorText(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };
  const ro = !isOwner;

  return (
    <div className="space-y-4 px-6 py-6">
      <Panel
        title="How migrations run"
        actions={
          isOwner && (
            <>
              {flash && <Pill tone="ok">Saved</Pill>}
              <Btn onClick={save} disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</Btn>
            </>
          )
        }
      >
        <Row title="When a company fails in a batch" hint="Stop is safer; keep going finishes the rest and lists failures.">
          <select className={sel} disabled={ro} value={settings.stop_on_failure ? 'stop' : 'continue'} onChange={(e) => set('stop_on_failure', e.target.value === 'stop')}>
            <option value="stop">Stop</option>
            <option value="continue">Keep going</option>
          </select>
        </Row>
        <Row title="Back up before running" hint={s3?.configured ? 'To S3, verified, per company and the registry.' : 'Verified, per company and the registry. On this server\'s disk until BACKUP_S3_* is set.'}>
          <select className={sel} disabled={ro} value={settings.backup} onChange={(e) => set('backup', e.target.value as RunnerSettings['backup'])}>
            <option value="always">Always</option>
            <option value="ask">Ask each time (off by default)</option>
          </select>
        </Row>
        <Row title="Statement time limit" hint="Stops a statement that runs too long (MariaDB max_statement_time).">
          <select className={sel} disabled={ro} value={settings.statement_timeout_s} onChange={(e) => set('statement_timeout_s', Number(e.target.value))}>
            {[60, 300, 600, 1800, 3600].map((s) => <option key={s} value={s}>{s < 3600 ? `${s / 60} min` : '1 hour'}</option>)}
            {![60, 300, 600, 1800, 3600].includes(settings.statement_timeout_s) && <option value={settings.statement_timeout_s}>{settings.statement_timeout_s} s</option>}
          </select>
        </Row>
        <Row title="Lock wait" hint="Gives up instead of blocking the app if a table is busy.">
          <select className={sel} disabled={ro} value={settings.lock_wait_s} onChange={(e) => set('lock_wait_s', Number(e.target.value))}>
            {[10, 30, 60, 120].map((s) => <option key={s} value={s}>{s} s</option>)}
            {![10, 30, 60, 120].includes(settings.lock_wait_s) && <option value={settings.lock_wait_s}>{settings.lock_wait_s} s</option>}
          </select>
        </Row>
        <Row title="Block files that can't run twice" hint="Needs IF NOT EXISTS on added columns, tables and indexes. A batch can still override it explicitly.">
          <select className={sel} disabled={ro} value={settings.block_non_idempotent ? 'block' : 'warn'} onChange={(e) => set('block_non_idempotent', e.target.value === 'block')}>
            <option value="block">Block</option>
            <option value="warn">Warn only</option>
          </select>
        </Row>
        <Row title="Tell me when a batch ends" hint="Saved now; email and the super-admin bell arrive in a later step.">
          <select className={sel} disabled={ro} value={settings.notify} onChange={(e) => set('notify', e.target.value as RunnerSettings['notify'])}>
            <option value="failures">Failures only</option>
            <option value="all">Every batch</option>
            <option value="off">Off</option>
          </select>
        </Row>
        <Row title="Archive folders older than" hint="Folders dated before today minus this many days leave every list (status, pending, stuck, counts). Un-archive one from Migration files > View archived. History keeps everything.">
          <select className={sel} disabled={ro} value={settings.archive_older_than_days ?? 30} onChange={(e) => set('archive_older_than_days', Number(e.target.value))}>
            {[0, 14, 30, 60, 90, 180].map((d) => <option key={d} value={d}>{d === 0 ? 'Off' : `${d} days`}</option>)}
            {![0, 14, 30, 60, 90, 180].includes(settings.archive_older_than_days ?? 30) && <option value={settings.archive_older_than_days}>{settings.archive_older_than_days} days</option>}
          </select>
        </Row>
        {ro && <p className="pt-2 text-xs text-slate-500">Only owners can change these.</p>}
        {error && <ErrorBox>{error}</ErrorBox>}
      </Panel>

      <Panel title="Runner status">
        <div className="grid gap-2 text-[13px] sm:grid-cols-2">
          <div>Batches and resume: {schema?.batches && schema.runsExtended ? <Pill tone="ok">ready</Pill> : <Pill tone="warn">needs the runner migration</Pill>}</div>
          <div>Per-statement history: {schema?.statementRuns ? <Pill tone="ok">on</Pill> : <Pill tone="warn">off</Pill>}</div>
          <div>Registry backups: {schema?.backupsExtended ? <Pill tone="ok">on</Pill> : <Pill tone="warn">needs the runner migration</Pill>}</div>
          <div>Backup storage: {s3?.configured ? <Pill tone="ok">S3 · {s3.bucket}</Pill> : <Pill tone="warn">this server's disk</Pill>}</div>
        </div>
      </Panel>
    </div>
  );
};

export default SettingsTab;
